/** Route access evaluation — dynamic permission contract and policy resolution. */

import { evaluatePermissionActionState, HUB_PERMISSION_KEYS, type HubPermission } from "@/lib/auth/permissions";
import { ROUTES, normalizeLegacyRoutePath } from "./route-paths";
import {
  SIDEBAR_NAV,
  type SidebarNavItem,
  ANALYSIS_REQUIRED_PERMISSIONS,
  RANKING_REQUIRED_PERMISSIONS,
  PERM_INTAKE_ITEMS_READ,
  PERM_PLATFORM_SEARCH,
  PERM_OBSERVABILITY_READ,
  PERM_DRAFT_READ,
  PERM_INTAKE_LEDGER_READ,
  PERM_CONVERSATION_OPS_READ,
  PERM_COMMERCIAL_PARTY_READ,
  PERM_ADMIN_ACCESS,
  PERM_PROMPT_TEMPLATES_READ,
  PERM_STRATEGY_DASHBOARD_READ,
  PERM_STRATEGY_GOALS_READ,
  PERM_TASKS_EXECUTE,
  PERM_TASKS_MANAGE,
  PERM_SETTINGS_INTEGRATIONS_READ,
} from "./sidebar-policy";

export const ROUTE_ACTION_POLICIES = {
  customerDelete: {
    requiredPermissions: [HUB_PERMISSION_KEYS.COMMERCIAL_PARTY_MANAGE, HUB_PERMISSION_KEYS.PROJECTS_DELETE],
    permissionMatch: "any",
  },
  projectDelete: {
    requiredPermissions: [HUB_PERMISSION_KEYS.PROJECTS_DELETE],
    permissionMatch: "all",
  },
} as const satisfies Record<string, RouteAccessPolicy>;


export interface RouteAccessPolicy {
  requiredPermissions?: readonly HubPermission[];
  permissionMatch?: "any" | "all";
}

export interface RouteAccessEvaluator {
  authenticated: boolean;
  policySnapshotReady?: boolean;
  policySnapshotError?: boolean;
  hasAnyPermission: (permissions: readonly HubPermission[]) => boolean;
}

export type PermissionAccessState = "allowed" | "pending" | "denied";

const DYNAMIC_ROUTE_PERMISSION_MAP = new Map<string, readonly HubPermission[]>();
const ROUTE_FALLBACK_PERMISSION_MAP = new Map<string, readonly HubPermission[]>([
  [ROUTES.WORK_PROJECTS, [HUB_PERMISSION_KEYS.PROJECTS_READ]],
  [ROUTES.SEARCH, PERM_PLATFORM_SEARCH],
  [ROUTES.INTAKE_DASHBOARD, PERM_INTAKE_ITEMS_READ],
  [ROUTES.INTAKE_DRAFTS, PERM_DRAFT_READ],
  [ROUTES.INTAKE_DIGEST, PERM_DRAFT_READ],
  [ROUTES.MAILBOX, PERM_SETTINGS_INTEGRATIONS_READ],
  [ROUTES.PLATFORM_ANALYTICS, PERM_OBSERVABILITY_READ],
  [ROUTES.PLATFORM_MONITORING, PERM_OBSERVABILITY_READ],
  [ROUTES.INTAKE_RECORDS, PERM_INTAKE_ITEMS_READ],
  [ROUTES.PARTIES, PERM_COMMERCIAL_PARTY_READ],
  [ROUTES.PARTY_IDENTITY_REVIEW, PERM_COMMERCIAL_PARTY_READ],
  [ROUTES.SUPPLIER_DIRECTORY, PERM_COMMERCIAL_PARTY_READ],
  [ROUTES.SUPPLIER_IDENTITY_REVIEW, PERM_COMMERCIAL_PARTY_READ],
  [ROUTES.PARTNER_DIRECTORY, PERM_COMMERCIAL_PARTY_READ],
  [ROUTES.PARTNER_IDENTITY_REVIEW, PERM_COMMERCIAL_PARTY_READ],
  [ROUTES.CONVERSATIONS, PERM_CONVERSATION_OPS_READ],
  [ROUTES.CONVERSATIONS_CHANNEL_SCOPE, PERM_CONVERSATION_OPS_READ],
  [ROUTES.WORK_TASKS, PERM_TASKS_EXECUTE],
  [ROUTES.WORK_CUSTOM_VIEW, PERM_TASKS_MANAGE],
  [ROUTES.WORK_ANALYTICS, PERM_TASKS_EXECUTE],
  [ROUTES.ADMIN_AUTHZ, ["authz.catalog.read"]],
  [ROUTES.ADMIN_AUTHZ_TENANTS, ["authz.tenants.read"]],
  [ROUTES.ADMIN_AUTHZ_SERVICES, ["authz.catalog.read"]],
  [ROUTES.ADMIN_AUTHZ_SERVICE_ITEMS, ["authz.catalog.read"]],
  [ROUTES.ADMIN_AUTHZ_SYNC, ["authz.service_items.write"]],
  [ROUTES.ADMIN_AUTHZ_AUDIT, ["authz.audit.read"]],
  [ROUTES.ADMIN_AUTHZ_SIMULATOR, ["authz.scope.read"]],
  [ROUTES.ADMIN_AUTHZ_ROLES, ["authz.roles.read"]],
  [ROUTES.ADMIN_AUTHZ_USERS, ["authz.users.read"]],
  [ROUTES.GOVERNANCE_RULES, PERM_PROMPT_TEMPLATES_READ],
  [ROUTES.GOVERNANCE_AI_PRIVACY_RULES, ["governance.llm_privacy.rules.read"]],
  [ROUTES.GOVERNANCE_DRAFT_TEMPLATES, PERM_PROMPT_TEMPLATES_READ],
  [ROUTES.GOVERNANCE_TRIAGE_TEMPLATES, PERM_PROMPT_TEMPLATES_READ],
  [ROUTES.GOVERNANCE_THREAD_DIGEST_TEMPLATES, PERM_PROMPT_TEMPLATES_READ],
  [ROUTES.GOVERNANCE_REQUEST_INFO_TEMPLATES, PERM_PROMPT_TEMPLATES_READ],
  [ROUTES.GOVERNANCE_TEMPLATE_ROUTING_RULES, PERM_PROMPT_TEMPLATES_READ],
  [ROUTES.GOVERNANCE_AI_AUDIT, ["governance.ai_audit.read"]],
  [ROUTES.GOVERNANCE_CONTEXT_CATALOG, ["knowledge.catalog.read"]],
  [ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT, ["knowledge.policy.read"]],
  [ROUTES.GOVERNANCE_CONTEXT_DRIFT, ["knowledge.catalog.read"]],
  [ROUTES.GOVERNANCE_CONTEXT_OPS, ["knowledge.ops.read"]],
  [ROUTES.CONTRACTS_TEMPLATES, ["contracts.templates.read"]],
  [ROUTES.CONTRACTS_AGREEMENTS, ["contracts.agreements.read"]],
  [ROUTES.STRATEGY, PERM_STRATEGY_DASHBOARD_READ],
  [ROUTES.STRATEGY_GOALS, PERM_STRATEGY_GOALS_READ],
  [ROUTES.STRATEGY_ALIGNMENT, PERM_STRATEGY_GOALS_READ],
  [ROUTES.STRATEGY_REVIEWS, PERM_STRATEGY_GOALS_READ],
  [ROUTES.FINANCE_INVOICES, ["finance.invoices.read"]],
  [ROUTES.FINANCE_PAYABLES, ["finance.payables.read"]],
  [ROUTES.FINANCE_BILLING, ["finance.payment_dossiers.read"]],
  [ROUTES.FINANCE_PAYMENT_DOSSIERS, ["finance.payment_dossiers.read"]],
  [ROUTES.FINANCE_BANK, ["finance.bank_accounts.read", "finance.bank_transactions.read"]],
  [ROUTES.FINANCE_BANK_TRANSACTIONS, ["finance.bank_accounts.read", "finance.bank_transactions.read"]],
  [ROUTES.FINANCE_RECONCILIATION, ["finance.reconciliation.read"]],
  [ROUTES.FINANCE_REPORTING, ["finance.ar_aging.read", "finance.journal_entries.read", "finance.events.read", "finance.invoices.read"]],
  [ROUTES.FINANCE_CONTENT_REVIEW, ["content.items.read", "content.review.approve"]],
  [ROUTES.HRM_LEGAL_ENTITIES, ["hrm.org.legal_entity.read"]],
  [ROUTES.HRM_COMPANY_LEVELS, ["hrm.org.company_level.read"]],
  [ROUTES.HRM_BUSINESS_LINES, ["hrm.org.business_line.read"]],
  [ROUTES.HRM_EMPLOYEE_GROUPS, ["hrm.org.employee_group.read"]],
  [ROUTES.HRM_SPECIALTIES, ["hrm.org.specialty.read"]],
  [ROUTES.HRM_POSITIONS, ["hrm.org.position.read"]],
  [ROUTES.HRM_HEADCOUNTS, ["hrm.org.headcount.read"]],
  [ROUTES.HRM_EMPLOYEES, ["hrm.hr.employee.read"]],
  [ROUTES.KNOWLEDGE_CORPUS, ["rag.documents.read"]],
  [ROUTES.KNOWLEDGE_PRE_RAG_REVIEW, ["rag.documents.read", "knowledge.ops.read"]],
  [ROUTES.KNOWLEDGE_INGEST, ["ingest.sources.read"]],
  [ROUTES.KNOWLEDGE_CONTENT, ["content.items.read"]],
  [ROUTES.KNOWLEDGE_CONTENT_CAPTURE, ["content.items.write"]],
  [ROUTES.KNOWLEDGE_CONTENT_REVIEW, ["content.items.read", "content.review.approve"]],
  [ROUTES.KNOWLEDGE_CONTENT_LIBRARY, ["content.items.read"]],
  [ROUTES.KNOWLEDGE_CONTENT_STORAGE, ["content.items.read"]],
  [ROUTES.KNOWLEDGE_OPS, ["rag.statistics.read"]],
  [ROUTES.KNOWLEDGE_SEARCH_LAB, ["rag.documents.read"]],
  [ROUTES.KNOWLEDGE_EMBEDDINGS, ["rag.embeddings.write"]],
  [ROUTES.KNOWLEDGE_EVALUATION, ["rag.evaluation.write"]],
  [ROUTES.AI_CHAT, ["ai.chat.execute"]],
  [ROUTES.AI_MODELS, ["ai.models.read"]],
  [ROUTES.AI_GENERATE, ["ai.generate.execute"]],
  [ROUTES.AI_ANALYSIS, ANALYSIS_REQUIRED_PERMISSIONS],
  [ROUTES.AI_NICE_TRAVEL, ["ai.booking_agent.execute"]],
  [ROUTES.AI_WORKFLOW_POLICY, ["llm.catalog.read"]],
  [ROUTES.RANKING, RANKING_REQUIRED_PERMISSIONS],
  [ROUTES.CONTENT, ["content.items.read"]],
  [ROUTES.CONTENT_CAPTURE, ["content.items.write"]],
  [ROUTES.CONTENT_REVIEW, ["content.items.read", "content.review.approve"]],
  [ROUTES.CONTENT_LIBRARY, ["content.items.read"]],
  [ROUTES.CONTENT_STORAGE, ["content.items.read"]],
  [ROUTES.RAG_DOCUMENTS, ["rag.documents.read"]],
  [ROUTES.RAG_INGEST, ["ingest.sources.read"]],
  [ROUTES.RAG_OPERATIONS, ["rag.documents.read"]],
  [ROUTES.GOVERNANCE_OPERATIONAL_SETTINGS, PERM_ADMIN_ACCESS],
  [ROUTES.ADMIN_OPERATIONAL, PERM_ADMIN_ACCESS],
  [ROUTES.ADMIN_INTEGRATIONS, PERM_ADMIN_ACCESS],
  [ROUTES.ADMIN_SYNC_DIAGNOSTICS, PERM_ADMIN_ACCESS],
  [ROUTES.INTAKE_LEDGER, PERM_INTAKE_LEDGER_READ],
  [ROUTES.ADMIN_CHECKLIST, PERM_ADMIN_ACCESS],
  [ROUTES.TECH_SPEC, PERM_ADMIN_ACCESS],
]);

function normalizeContractPath(path: string): string {
  const trimmed = String(path || "").split("?")[0] || "/";
  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  const normalized = withLeadingSlash !== "/" && withLeadingSlash.endsWith("/") ? withLeadingSlash.slice(0, -1) : withLeadingSlash;
  return normalizeLegacyRoutePath(normalized || "/");
}

function mergeRoutePermissions(previous: readonly HubPermission[] | undefined, incoming: readonly HubPermission[]): readonly HubPermission[] {
  return Array.from(new Set([...(previous || []), ...incoming]));
}

/**
 * Merges permissions per path from the intake route contract. The backend returns one row
 * per HTTP method; the same path (e.g. /tasks) must not be reduced to a single method's
 * requirements — otherwise POST (tasks.manage) can overwrite GET (tasks.execute) and
 * incorrectly block members from the Tasks page.
 */
export interface DynamicRoutePermissionContractEntry {
  path?: unknown;
  permission_key?: unknown;
  required_permissions?: unknown;
}

function contractEntryPermissions(entry: DynamicRoutePermissionContractEntry): HubPermission[] {
  const permissions: string[] = [];
  if (Array.isArray(entry.required_permissions)) {
    for (const permission of entry.required_permissions) {
      const normalized = String(permission).trim();
      if (normalized.length > 0) {
        permissions.push(normalized);
      }
    }
  }
  if (typeof entry.permission_key === "string") {
    const normalized = entry.permission_key.trim();
    if (normalized.length > 0) {
      permissions.push(normalized);
    }
  }
  return Array.from(new Set(permissions)) as HubPermission[];
}

export function setDynamicRoutePermissionContract(entries: readonly DynamicRoutePermissionContractEntry[]): void {
  DYNAMIC_ROUTE_PERMISSION_MAP.clear();
  for (const entry of entries) {
    const normalizedPath = normalizeContractPath(typeof entry.path === "string" ? entry.path : "");
    const requiredPermissions = contractEntryPermissions(entry);
    const aliases = [normalizedPath];
    if (normalizedPath === "/draft" || normalizedPath.startsWith("/draft/")) {
      aliases.push(normalizedPath.replace("/draft", "/drafts"));
    }
    for (const alias of aliases) {
      const merged = mergeRoutePermissions(DYNAMIC_ROUTE_PERMISSION_MAP.get(alias), requiredPermissions);
      DYNAMIC_ROUTE_PERMISSION_MAP.set(alias, merged);
    }
  }
}

export function clearDynamicRoutePermissionContract(): void {
  DYNAMIC_ROUTE_PERMISSION_MAP.clear();
}

function normalizePathnameForPolicy(pathname: string): string {
  const raw = String(pathname || "").split("?")[0] || "/";
  const normalized = raw !== "/" && raw.endsWith("/") ? raw.slice(0, -1) : raw;
  return normalizeLegacyRoutePath(normalized || "/");
}

function routePolicyFromNavHref(pathname: string): RouteAccessPolicy | null {
  const normalizedPath = normalizePathnameForPolicy(pathname);
  let staticPermissions: readonly HubPermission[] | null = null;
  let staticSelectedLen = -1;
  for (const [path, permissions] of ROUTE_FALLBACK_PERMISSION_MAP.entries()) {
    if (normalizedPath === path || normalizedPath.startsWith(`${path}/`)) {
      if (path.length > staticSelectedLen) {
        staticPermissions = permissions;
        staticSelectedLen = path.length;
      }
    }
  }
  if (staticPermissions) {
    return {
      requiredPermissions: staticPermissions,
      permissionMatch: normalizedPath === ROUTES.RANKING ? "all" : "any",
    };
  }
  let dynamicSelectedPermissions: readonly HubPermission[] | null = null;
  let dynamicSelectedLen = -1;
  for (const [path, permissions] of DYNAMIC_ROUTE_PERMISSION_MAP.entries()) {
    if (normalizedPath === path || normalizedPath.startsWith(`${path}/`)) {
      if (path.length > dynamicSelectedLen) {
        dynamicSelectedPermissions = permissions;
        dynamicSelectedLen = path.length;
      }
    }
  }
  if (dynamicSelectedPermissions) {
    return {
      requiredPermissions: dynamicSelectedPermissions,
    };
  }
  let selected: SidebarNavItem | null = null;
  let selectedLen = -1;
  for (const item of SIDEBAR_NAV) {
    if (normalizedPath === item.href || normalizedPath.startsWith(`${item.href}/`)) {
      if (item.href.length > selectedLen) {
        selected = item;
        selectedLen = item.href.length;
      }
    }
  }
  if (!selected) return null;
  return {
    requiredPermissions: selected.requiredPermissions,
  };
}

export function getSidebarRoutePolicy(pathname: string): RouteAccessPolicy | null {
  return routePolicyFromNavHref(pathname);
}

export function evaluatePermissionAccessState(permissionRules: readonly HubPermission[] | undefined, evaluator: RouteAccessEvaluator, permissionMatch: "any" | "all" = "any"): PermissionAccessState {
  return evaluatePermissionActionState(permissionRules ?? [], permissionMatch, {
    authenticated: evaluator.authenticated,
    policySnapshotReady: evaluator.policySnapshotReady !== false,
    policySnapshotError: evaluator.policySnapshotError === true,
  }, (permission) => evaluator.hasAnyPermission([permission]));
}

function evaluatePermissionAccess(permissionRules: readonly HubPermission[] | undefined, evaluator: RouteAccessEvaluator, permissionMatch: "any" | "all" = "any"): boolean {
  return evaluatePermissionAccessState(permissionRules, evaluator, permissionMatch) !== "denied";
}

export function isSidebarNavItemAccessible(item: SidebarNavItem, evaluator: RouteAccessEvaluator): boolean {
  const policy = routePolicyFromNavHref(item.href);
  return evaluatePermissionAccess(policy?.requiredPermissions ?? item.requiredPermissions, evaluator, policy?.permissionMatch);
}

export function isRouteAccessible(pathname: string, evaluator: RouteAccessEvaluator): boolean {
  const policy = routePolicyFromNavHref(pathname);
  return evaluatePermissionAccess(policy?.requiredPermissions, evaluator, policy?.permissionMatch);
}
